import { map, tap } from 'rxjs/operators';
import { TFunction } from 'react-i18next';
import DealType from '../../../../enums/deal-type';
import { Option } from '../../../../components/form/elements/select';
import { ObservableOptions } from '../../../../components/form/elements/select/async-select';
import Classifier from '../../../../enums/classifier';
import { ProductObtainType } from '../../../../enums/product-obtain-type';
import { Product } from '../../../../interfaces/product';
import { ProductService } from '../../../../services/product.service';
import { isObject } from '../../../../services/utils';
import { categoryDataMap } from './async-categories';
import { typeDataMap } from './async-types';

export function productDataMap(v: Product) {
  return {
    label: v.name,
    value: v.id,
    obtain_type_id: v.obtain_type_id,
    category: categoryDataMap(v.category),
    type: typeDataMap(v.type),
    unit: { label: v.unit, value: v.unit },
    price: v.planned_price,
  };
}

const OUTGOING_DEALS = [
  DealType.sold,
  DealType.self_consumed,
  DealType.recycled,
  DealType.spent,
  DealType.lost,
  DealType.moved,
];

export default function asyncProducts({
  productService,
  farmId,
  classifierKeys = [
    Classifier.PRODUCT_CATEGORIES_AND_TYPES,
    Classifier.OBTAINED_PRODUCT_CATEGORIES_AND_TYPES,
  ],
  t,
  dealType,
}: {
  productService: ProductService;
  farmId: string;
  classifierKeys?: Classifier[];
  t: TFunction;
  dealType: DealType;
}): ObservableOptions {
  return (
    value: string,
    loadedOptions: Option[],
    { parentName, page, getValues, setValue }: any
  ) => {
    const showClassifiers =
      dealType === DealType.purchased || dealType === DealType.produced;

    if (!showClassifiers) {
      classifierKeys = classifierKeys.filter(
        (key) => key !== Classifier.OBTAINED_PRODUCT_CATEGORIES_AND_TYPES
      );
    }

    let productObtainType = parentName
      ? getValues(parentName.concat('.product_obtain_type_id'))
      : getValues().product_obtain_type_id;

    if (isObject(productObtainType)) {
      productObtainType = (productObtainType as unknown as Option)
        ?.value as ProductObtainType;
    }

    const params: any = { page };

    const productCategoryId = getValues(
      parentName
        ? parentName.concat('.product_category_id')
        : 'product_category_id'
    )?.value;

    const productTypeId = getValues(
      parentName ? parentName.concat('.product_type_id') : 'product_type_id'
    )?.value;

    if (productCategoryId) {
      params.category_id = productCategoryId;
    }

    if (productTypeId) {
      params.type_id = productTypeId;
    }

    if (
      !productObtainType ||
      productObtainType === ProductObtainType.RECYCLE_LOST
    ) {
      params.obtain_type_id = null;
    } else if (
      productObtainType !== ProductObtainType.RECYCLE_GAINED &&
      classifierKeys.includes(Classifier.OBTAINED_PRODUCT_CATEGORIES_AND_TYPES)
    ) {
      params.obtain_type_id = [
        productObtainType,
        ProductObtainType.RECYCLE_GAINED,
      ];
    } else {
      params.obtain_type_id = [productObtainType];
    }

    // Adding search parameter
    params.search = value;

    return productService.usedProducts(farmId, params).pipe(
      map((response) => {
        const { products, classifiers, types } = response;

        const mappedTypes = types.map(typeDataMap);

        const classifierValues = classifiers?.data?.map(
          (classifier) => classifier?.classifier_value?.value
        );

        // Filter products based on search value
        const filteredProducts = products.data.filter((product) =>
          product.name.toLowerCase().includes(value.toLowerCase())
        );

        // Map filtered products to desired format
        const mappedProducts: Option[] = filteredProducts.map(productDataMap);

        // Filter classifiers based on search value and showClassifiers flag
        let mappedClassifiers: Option[] = [];
        if (showClassifiers) {
          const filteredClassifiers = classifierValues?.filter((classifier) =>
            classifier.toLowerCase().includes(value.toLowerCase())
          );

          const productLabels = mappedProducts.map((product) => product.label);

          const filteredOutExistingProducts = filteredClassifiers?.filter(
            (classifier) => !productLabels.includes(classifier)
          );

          // Map filtered classifiers to desired format
          mappedClassifiers = filteredOutExistingProducts?.map((classifier) => {
            const classifierTypeId = classifiers.data.find(
              ({ classifier_value: { value } }) => value === classifier
            )?.type_id;

            const classifierType = mappedTypes.find(
              (type) => type.value === classifierTypeId
            );

            return {
              value: classifier,
              label: classifier,
              type: classifierType,
              __isNew__: true,
            };
          });
        }

        return {
          hasMore:
            products.current_page !== products.last_page ||
            classifiers.current_page !== classifiers.last_page,
          options: [
            {
              label: t('pages.warehouse.deals.used-products'),
              options: mappedProducts,
            },
            {
              label: t('pages.warehouse.deals.products'),
              options: mappedClassifiers ?? [],
            },
          ],
          additional: { page: products.current_page + 1 },
        };
      }),
      tap((response) => {
        if (
          OUTGOING_DEALS.includes(dealType) &&
          response?.options[0]?.options.length === 1 &&
          getValues('product_category_id').label &&
          getValues('product_type_id').label
        ) {
          if (getValues('unit').value === '') {
            setValue('unit', response.options[0].options[0].unit);
          }
          setValue('product_id', response.options[0].options[0], {
            shouldValidate: true,
          });
          setValue(
            'product_obtain_type_id',
            response.options[0].options[0].obtain_type_id
          );
        }
      })
    );
  };
}
